import { BarcodeScanner } from "@capacitor-community/barcode-scanner";
import { useEffect, useState } from "react";
import { App } from "@capacitor/app";

const CAMERA_TIMEOUT_IN_MS = 10000; //10 seconds

// ✅ Check if camera permissions are granted
const checkCameraPermission = async (): Promise<boolean> => {
  const permission = await BarcodeScanner.checkPermission({ force: true });

  if (!permission.granted) {
    console.warn("Camera permission denied");
    return false;
  }

  return true;
};

export function useBarcodeManager(onScan?: (content: string) => void) {
  const [scanText, setScanText] = useState<string>("");
  const [honeywellFallback, setHoneywellFallback] = useState(false);

  const stopScan = async () => {
    setHoneywellFallback(false);
    await BarcodeScanner.stopScan();
    document.body.classList.remove("qrscanner");
  };

  /**
   * Honeywell CT40 scanners typically work in two modes:
      1. Keyboard Wedge Mode (Default) → Scans act as keyboard input
      2. Intent API Mode → Sends scans via Android intents
    We will listen for both as a fallback.
   */

  // ✅ Listen for Honeywell Scanner Input (Keyboard Wedge Mode)
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Enter" && scanText) {
        console.log("Honeywell Scanner Data:", scanText);
      } else {
        setScanText((prev) => (prev ? prev + event.key : event.key));
        stopScan();
      }
    };

    if (honeywellFallback) {
      window.addEventListener("keydown", handleKeyPress);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [scanText, honeywellFallback]);

  // ✅ Listen for Honeywell Scanner (Intent API Mode)
  useEffect(() => {
    App.addListener("appUrlOpen", (event) => {
      const url = new URL(event.url);
      const barcodeValue = url.searchParams.get("barcode");
      if (barcodeValue) {
        setScanText(barcodeValue);
        onScan?.(barcodeValue);
        stopScan();
      }
    });
  }, [onScan]);

  async function scanClick(): Promise<void> {
    await BarcodeScanner.prepare();
    startScan();
  }

  // ✅ Enable Honeywell Fallback (Hardware Scanner)
  const enableHoneywellFallback = () => {
    setHoneywellFallback(true);
  };

  const startScan = async () => {
    const hasPermission = await checkCameraPermission();
    if (!hasPermission) {
      console.warn("Camera scanning not permitted. Falling back...");
      enableHoneywellFallback();
    }

    try {
      document.body.classList.add("qrscanner");
      const scanPromise = BarcodeScanner.startScan();

      // ✅ Timeout after 7 seconds if scanning does not work
      const timeoutPromise = new Promise(
        (resolve) => setTimeout(() => resolve(null), CAMERA_TIMEOUT_IN_MS) //will wait for camera to finish the scanning
      );

      const result = (await Promise.race([scanPromise, timeoutPromise])) as {
        hasContent: boolean;
        content?: string;
      } | null;

      if (result && result.hasContent) {
        if (result.hasContent) {
          setScanText(result.content ?? "");
          onScan?.(result.content ?? "");
        }
        stopScan();
      } else {
        console.log(
          "Scan timeout or failure. Stopping camera scanner and enabling honeywell fallback"
        );
        enableHoneywellFallback();
      }
    } catch (error) {
      console.log("Scanning error:", error);
      enableHoneywellFallback();
    }
  };

  return {
    scanText,
    setScanText,
    scanClick,
    stopScan,
  };
}
